import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { getSportStrategy } from '@/lib/sport-strategies'
import { apiClient } from '@/services/api'
import type { Sport } from '@/types/api'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Icon, ICONS } from '@/lib/icons'

interface TournamentMatchEditDialogProps {
  isOpen: boolean
  onClose: () => void
  onMatchUpdated: () => void
  matchId: string
  bracketId?: string // For knockout matches
  seriesId?: string // For group stage matches
  groupId?: string // For group stage matches
  currentScoreA: number
  currentScoreB: number
  playerAName: string
  playerBName: string
  isGroupStage: boolean
  sport: Sport
  setsToPlay?: number
}

export function TournamentMatchEditDialog({
  isOpen,
  onClose,
  onMatchUpdated,
  matchId,
  bracketId,
  seriesId,
  groupId,
  currentScoreA,
  currentScoreB,
  playerAName,
  playerBName,
  isGroupStage,
  sport,
  setsToPlay,
}: TournamentMatchEditDialogProps) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [scoreA, setScoreA] = useState<number>(currentScoreA)
  const [scoreB, setScoreB] = useState<number>(currentScoreB)

  useEffect(() => {
    if (isOpen) {
      setScoreA(currentScoreA)
      setScoreB(currentScoreB)
    }
  }, [isOpen, currentScoreA, currentScoreB])

  // Check if current scores are valid (for enabling/disabling submit button)
  const isScoreValid = () => {
    // Use sport-specific strategy to validate
    const strategy = getSportStrategy(sport)
    const validation = strategy.validateScore(scoreA, scoreB, {
      isKnockout: !isGroupStage,
      setsToPlay: setsToPlay
    })

    return validation.valid
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (isGroupStage) {
        // Edit group stage match
        if (!seriesId || !groupId) {
          throw new Error('Missing seriesId or groupId for group stage match')
        }
        await apiClient.editGroupStageMatch(seriesId, groupId, matchId, scoreA, scoreB)
      } else {
        // Edit knockout match
        if (!bracketId) {
          throw new Error('Missing bracketId for knockout match')
        }
        await apiClient.editKnockoutMatch(bracketId, matchId, scoreA, scoreB)
      }

      toast.success(t('tournament.edit.success'))
      onMatchUpdated()
      onClose()
    } catch (error) {
      // Error logged for debugging
      // eslint-disable-next-line no-console
      console.error('Error editing match:', error)
      toast.error(t('tournament.edit.error'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] p-0">
        <div className="p-6 pb-0">
          <DialogHeader>
            <DialogTitle>{t('tournament.edit.title')}</DialogTitle>
            <DialogDescription>
              {t('tournament.edit.warning')}
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <DialogBody className="px-6">
            <div className="grid gap-4">
              {/* Player A Score */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="scoreA" className="text-right col-span-2">
                  {playerAName}
                </Label>
                <Input
                  id="scoreA"
                  type="number"
                  min="0"
                  max="5"
                  value={scoreA}
                  onChange={(e) => setScoreA(parseInt(e.target.value, 10) || 0)}
                  className="col-span-2"
                  required
                />
              </div>

              {/* Player B Score */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="scoreB" className="text-right col-span-2">
                  {playerBName}
                </Label>
                <Input
                  id="scoreB"
                  type="number"
                  min="0"
                  max="5"
                  value={scoreB}
                  onChange={(e) => setScoreB(parseInt(e.target.value, 10) || 0)}
                  className="col-span-2"
                  required
                />
              </div>

              {/* Warning message */}
              {!isGroupStage && (
                <div className="flex items-start gap-2 p-3 bg-warning-muted border border-warning/30 rounded-md">
                  <Icon icon={ICONS.alertCircle} width={20} height={20} className="text-warning mt-0.5" />
                  <p className="text-sm text-warning-foreground">
                    {t('tournament.edit.warning')}
                  </p>
                </div>
              )}
            </div>
          </DialogBody>

          <div className="px-6 pb-6">
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={loading || !isScoreValid()}>
                {loading ? t('common.saving') : t('common.save')}
              </Button>
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
